import { useCallback, useState, useRef, useEffect } from 'react';
import { useViewportStore } from '../../stores/viewportStore';
import { X, Lock } from 'lucide-react';
import type { GuideLine } from '../../types';

interface GuideLinesOverlayProps {
  artboardLeft: number;
  artboardTop: number;
  containerWidth: number;
  containerHeight: number;
}

export default function GuideLinesOverlay({
  artboardLeft,
  artboardTop,
  containerWidth,
  containerHeight,
}: GuideLinesOverlayProps) {
  const { guides, showGuides, zoom, updateGuide, removeGuide } = useViewportStore();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const dragStartRef = useRef<{ startPos: number; startGuidePos: number } | null>(null);

  const handleDragStart = useCallback(
    (guide: GuideLine, e: React.MouseEvent) => {
      if (guide.locked) return;
      e.preventDefault();
      e.stopPropagation();
      setDraggingId(guide.id);
      const screenPos = guide.orientation === 'horizontal' ? e.clientY : e.clientX;
      dragStartRef.current = { startPos: screenPos, startGuidePos: guide.position };
    },
    [],
  );

  const handleDragMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingId || !dragStartRef.current) return;
      const guide = useViewportStore.getState().guides.find((g) => g.id === draggingId);
      if (!guide) return;

      const currentPos = guide.orientation === 'horizontal' ? e.clientY : e.clientX;
      const delta = (currentPos - dragStartRef.current.startPos) / zoom;
      const newPosition = Math.round(dragStartRef.current.startGuidePos + delta);

      updateGuide(draggingId, { position: newPosition });
    },
    [draggingId, zoom, updateGuide],
  );

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (draggingId) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [draggingId, handleDragMove, handleDragEnd]);

  if (!showGuides || guides.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      {guides.map((guide) => {
        const isHovered = hoveredId === guide.id || draggingId === guide.id;
        const screenPos =
          guide.orientation === 'horizontal'
            ? artboardTop + guide.position * zoom
            : artboardLeft + guide.position * zoom;

        if (guide.orientation === 'horizontal' && (screenPos < -20 || screenPos > containerHeight + 20)) return null;
        if (guide.orientation === 'vertical' && (screenPos < -20 || screenPos > containerWidth + 20)) return null;

        return (
          <div key={guide.id}>
            <div
              className="absolute pointer-events-auto"
              style={
                guide.orientation === 'horizontal'
                  ? {
                      left: 0,
                      right: 0,
                      top: screenPos - 1,
                      height: 3,
                      cursor: guide.locked ? 'not-allowed' : 'row-resize',
                    }
                  : {
                      top: 0,
                      bottom: 0,
                      left: screenPos - 1,
                      width: 3,
                      cursor: guide.locked ? 'not-allowed' : 'col-resize',
                    }
              }
              onMouseDown={(e) => handleDragStart(guide, e)}
              onMouseEnter={() => setHoveredId(guide.id)}
              onMouseLeave={() => !draggingId && setHoveredId(null)}
            >
              <div
                className="absolute transition-opacity"
                style={
                  guide.orientation === 'horizontal'
                    ? {
                        left: 0,
                        right: 0,
                        top: 1,
                        height: 1,
                        background: guide.color,
                        opacity: isHovered ? 1 : 0.6,
                        boxShadow: isHovered ? `0 0 4px ${guide.color}` : 'none',
                      }
                    : {
                        top: 0,
                        bottom: 0,
                        left: 1,
                        width: 1,
                        background: guide.color,
                        opacity: isHovered ? 1 : 0.6,
                        boxShadow: isHovered ? `0 0 4px ${guide.color}` : 'none',
                      }
                }
              />
            </div>

            {isHovered && (
              <div
                className="absolute pointer-events-auto z-20 flex items-center gap-1"
                style={
                  guide.orientation === 'horizontal'
                    ? { left: 8, top: screenPos + 4 }
                    : { left: screenPos + 4, top: 8 }
                }
              >
                <span
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                  style={{
                    background: `${guide.color}20`,
                    color: guide.color,
                    border: `1px solid ${guide.color}40`,
                  }}
                >
                  {guide.position}px
                </span>

                {guide.locked && (
                  <Lock className="w-3 h-3" style={{ color: guide.color }} />
                )}

                {!guide.locked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeGuide(guide.id);
                    }}
                    className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                    title="Remove guide"
                  >
                    <X className="w-3 h-3" style={{ color: guide.color }} />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
